import * as React from 'react';

import { DropboxDocumentEntity } from 'jamespot-user-api';
import { useIntl } from 'react-intl';
import { JRCLoader } from '../../JRCLoader/JRCLoader';
import { useCallback } from 'react';
import { JRCInputSearchRaw } from '../../Form/Input/JRCInputSearchRaw/JRCInputSearchRaw';
import styled from 'styled-components';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import { EntityItem } from '../components/EntityItem';
import { DriveIsEmpty } from '../components/DriveIsEmpty';
import { useAbortController } from '../../../hooks/useAbortController';

const SearchWrapper = styled.div`
    margin-bottom: ${({ theme }) => theme.space.md}px;
`;

export const LoaderWrapper = styled.div`
    margin-top: ${({ theme }) => theme.space.l}px;
`;

type DropboxDocumentsProps = {
    path: string;
    initialFolderColor?: string;
    useNavigation: boolean;
    useFilter: boolean;
    useDocumentFilters: string[];
    onClick: (entity: DropboxDocumentEntity) => void;
    onSelect?: (entity: DropboxDocumentEntity) => void;
    onSelectDocument?: (entity: DropboxDocumentEntity) => void;
    onSelectMultiDocument?: (entity: DropboxDocumentEntity) => void;
    onError?: (code: string) => void;
    handlers: {
        drive: Pick<JDriveApi, 'dropboxDocuments'>;
    };
    id: string;
    service: string;
};

export const DropboxDocuments = (props: DropboxDocumentsProps) => {
    const abortController = useAbortController();
    const intl = useIntl();
    const {
        initialFolderColor,
        useNavigation,
        useDocumentFilters,
        onSelect,
        onSelectDocument,
        onSelectMultiDocument,
        useFilter,
        onClick,
        id,
        path,
    } = props;

    const [areDocumentsFetched, setAreDocumentsFetched] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [entities, setEntities] = React.useState<DropboxDocumentEntity[]>([]);
    const [lastPath, setLastPath] = React.useState<string>();

    const { onError } = props;
    const fetchDocuments = useCallback(
        (query: string) => {
            setLoading(true);
            props.handlers.drive
                .dropboxDocuments(
                    {
                        documentParams: { path_lower: path, query },
                        id,
                    },
                    { signal: abortController.signal },
                )
                .then((resp) => {
                    setEntities(
                        resp.result.filter((el) =>
                            useDocumentFilters.length
                                ? useDocumentFilters.includes(el.id) && el.type === 'document'
                                : true,
                        ),
                    );
                })
                .catch((error) => {
                    if (onError && !abortController.signal.aborted) {
                        onError(error.errorMsg);
                    }
                })
                .finally(() => {
                    if (!abortController.signal.aborted) {
                        setLoading(false);
                    }
                });
        },
        [abortController.signal, id, onError, path, props.handlers.drive, useDocumentFilters],
    );

    React.useEffect(() => {
        if (!areDocumentsFetched || lastPath !== path) {
            fetchDocuments('');
            setAreDocumentsFetched(true);
            setLastPath(path);
        }
    }, [areDocumentsFetched, fetchDocuments, lastPath, path]);

    return (
        <>
            {useFilter && (
                <SearchWrapper>
                    <JRCInputSearchRaw
                        onChange={fetchDocuments}
                        width={'100%'}
                        placeholder={intl.formatMessage({ id: 'DRIVE_Input_Search_Group_Item' })}
                    />
                </SearchWrapper>
            )}
            {loading && (
                <LoaderWrapper>
                    <JRCLoader size={'m'} />
                </LoaderWrapper>
            )}
            {!loading && entities.length === 0 && <DriveIsEmpty />}
            {!loading &&
                entities.length !== 0 &&
                entities
                    .sort((a, b) => b.type.localeCompare(a.type))
                    .map((entity) => (
                        <EntityItem
                            key={entity.id}
                            entity={entity}
                            initialFolderColor={initialFolderColor}
                            useNavigation={useNavigation}
                            onSelect={onSelect}
                            onSelectDocument={onSelectDocument}
                            onSelectMultiDocument={onSelectMultiDocument}
                            onClick={() => onClick(entity)}
                        />
                    ))}
        </>
    );
};
